import { workedVisitsCard } from "./worked-visits";
import type { ScreenContext } from "../../shell/types";
import { foldRecord } from "../../ui/fold";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { firstPageParams, list, type Column } from "../../ui/list";
import type { PageInfo } from "../../../server/rules/list-page";
import { openModal } from "../../ui/modal";
import { card, chip, download as fileDownload, ib, table } from "../../ui/record";
import { area, refuse } from "../clients/forms";
import { pageHead } from "../empty";
import { openSend } from "../../ui/send";
import { invoiceClaimsCard } from "./claims";

// Home Care › Billing (brief 1E-1, mockup v54 homecare/billing and the invoice record HC-1112):
// - Bill the week: the week that closed last, per patient: visits, ready, flagged (why, from the
//   visit's exceptions, a hold or no bill rate) and the amount ready; the week arrows; Make the
//   week's invoices (Billing at Edit) makes one Draft per payer, flagged visits left out.
// - The invoices as a list (the default) or a board: Draft, Approved, Sent.
// - The invoice record at /homecare/billing/<id>: its stages (Draft → Approved → Sent → Paid →
//   Deposited) with the next step (Approve, Mark sent), Void with a reason as an icon button, the
//   invoice date, due date, terms and payment, billed and received, and the line by line table
//   (Who is the service, never the caregiver). Preview opens the invoice on the business's
//   letterhead, with the PDF to download or print.
// - Brief 1E-3: Received, Fees withheld and Short or denied from the postings; each line's Paid,
//   Fee, Status and Reason, and the postings that cover it; Paid when every line is settled, then
//   Mark deposited with the day; Get paid online (Stripe connected only): Send a pay link for the
//   open amount through the Send sheet.

interface Week {
  asOf: string;
  week: { start: string; end: string; label: string };
  closes: string;
  prev: string;
  next: string | null;
  canMake: boolean;
  rows: {
    patientId: string;
    patient: string;
    client: string;
    week: string | null;
    visits: number;
    ready: number;
    billed: number;
    flagged: string[];
    /** Brief 1E-15: nobody receives this client's invoices (a flag; the invoice can be made). */
    notice: string | null;
    amount: string;
  }[];
  readyTotal: string;
}
interface Packet {
  summary: string;
  pages: { page: string; what: string; status: "ready" | "in" | "missing" | "waiting" }[];
  missing: string | null;
  canSend: boolean;
}
interface Held {
  held: {
    visitId: string;
    day: string;
    visit: string;
    patient: string;
    caregiver: string;
    client: string;
    why: string;
    times: string;
  }[];
  canFix: boolean;
}
interface InvoiceRow {
  id: string;
  number: string;
  client: string;
  amount: string;
  period: string;
  due: { text: string; level: string };
  status: string;
  stage: string;
}
interface Invoice {
  id: string;
  number: string;
  client: { id: string | null; name: string };
  period: string;
  format: string;
  status: string;
  stage: number;
  stages: string[];
  invoiceDate: string | null;
  dueDate: string | null;
  terms: string;
  payment: { text: string; level: string };
  billed: string;
  received: string;
  fees: string;
  short: string;
  open: string;
  void: { reason: string; at: string } | null;
  madeBy: string;
  lines: {
    id: string;
    who: string;
    date: string;
    inOut: string;
    description: string;
    units: string;
    rate: string;
    billed: string;
    paid: string;
    fee: string;
    status: { text: string; level: string } | null;
    reason: string;
    patientId: string;
    voided: boolean;
    /** Brief 1E-16: the differentials, each its own line ("Weekend · 6.00 h × $3.00"). */
    parts?: { words: string; billed: string }[];
  }[];
  payments: { id: string; label: string; status: string; amount: string }[];
  /** Brief 1E-35: the claim lines as the contract sets them. */
  claimLines: {
    how: string;
    title: string;
    sub: string;
    billTo: string;
    period: string;
    lines: {
      key: string;
      day: string;
      shifts: string[];
      service: string;
      code: string;
      place: string;
      units: string;
      rate: string;
      amount: string;
    }[];
  };
  deposited: { on: string; by: string | null } | null;
  payOnline: {
    can: boolean;
    why: string | null;
    open: string;
    links: { id: string; amount: string; status: string; sent: string; expires: string }[];
  } | null;
  can: { approve: boolean; send: boolean; void: boolean; deposit: boolean };
}
interface Letter {
  number: string;
  draft: boolean;
  status: string;
  business: {
    name: string;
    line?: string | null;
    address: string[];
    phone: string | null;
    npi: string | null;
  };
  billTo: { name: string; vendor?: string | null; address: string[]; contract: string | null };
  period: string;
  issued: string;
  due: string | null;
  terms: string;
  patients: {
    patient: string;
    birthDate: string | null;
    claim: string | null;
    lines: {
      date: string;
      service: string;
      units: string;
      rate: string;
      amount: string;
      shifts?: string[];
    }[];
    total: string;
  }[];
  total: string;
}

const STAGES = ["Draft", "Approved", "Sent", "Paid", "Deposited", "Void"];
const STAGE_LEVEL: Record<string, string> = {
  Draft: "plain",
  Approved: "ok",
  Sent: "ok",
  Paid: "ok",
  Deposited: "ok",
  Void: "bad",
};
const STAGE_HINT = [
  "Built from validated visits. Check the lines, then approve it.",
  "Approved and locked. Mark it sent when it goes out.",
  "Sent. Payment is matched to it line by line when it comes in.",
  "Payment received: every line is settled. Mark it deposited on the deposit date.",
  "Deposited and reconciled.",
];

export async function billingScreen(host: HTMLElement, ctx: ScreenContext) {
  if (ctx.record) return invoiceRecord(host, ctx, ctx.record);
  host.append(
    pageHead(
      ctx,
      "Each client's format: invoice, CMS-1500 or electronic claim. Followed line by line to payment.",
    ),
  );
  const weekSlot = el("div");
  const listSlot = el("div");
  const heldSlot = el("div");
  // Brief 1E-33: worked visits, starting on Not billed.
  const worked = workedVisitsCard(ctx, "billing");
  // Brief 1E-35: the held visits on top.
  host.append(heldSlot, worked, weekSlot, listSlot);

  async function loadHeld() {
    const res = await api<Held>("GET", "/api/billing/held");
    if (!res.ok || !res.body.held.length) return heldSlot.replaceChildren();
    const h = res.body;
    const c = card(
      el("span", {}, icon("alert"), ` Held · ${h.held.length}`),
      "Not billed until the times are fixed.",
      [],
      table(
        [
          {
            h: "Visit",
            f: (x) =>
              el(
                "span",
                {},
                el("b", {}, x.visit),
                el("div", { class: "small muted" }, `${x.patient} · ${x.caregiver}`),
              ),
          },
          { h: "Client", f: (x) => x.client },
          {
            h: "Why",
            f: (x) =>
              el("span", {}, chip(x.why, "warn"), el("div", { class: "small muted" }, x.times)),
          },
          {
            h: "",
            f: (x) => {
              if (!h.canFix) return "";
              const b = el(
                "button",
                { type: "button", class: "btn sm" },
                icon("pencil"),
                " Fix the times",
              );
              b.addEventListener("click", () => void worked.fix(x.visitId, x.day));
              return b;
            },
          },
        ],
        h.held,
        undefined,
        "Held visits",
      ),
    );
    c.setAttribute("aria-label", "Held");
    heldSlot.replaceChildren(c);
  }
  void loadHeld();
  worked.addEventListener("wv-changed", () => void Promise.all([loadHeld(), loadWeek()]));
  let asOf: string | null = null;

  async function loadWeek() {
    const res = await api<Week>("GET", `/api/billing/week${asOf ? `?asOf=${asOf}` : ""}`);
    if (!res.ok) {
      weekSlot.replaceChildren(
        el("p", { class: "note", role: "alert" }, "Bill the week couldn't be loaded."),
      );
      return;
    }
    const w = res.body;
    const prev = ib("arrow-left", "The week before", () => {
      asOf = w.prev;
      void loadWeek();
    });
    const next = w.next
      ? ib("arrow-right", "The week after", () => {
          asOf = w.next;
          void loadWeek();
        })
      : null;
    const make =
      w.canMake && w.rows.some((r) => r.ready > 0)
        ? el(
            "button",
            { type: "button", class: "btn pri sm" },
            icon("zap"),
            " Make the week's invoices",
          )
        : null;
    make?.addEventListener("click", async () => {
      make.disabled = true;
      const r = await api<{ made: string[]; added: string[] }>(
        "POST",
        "/api/billing/week/invoices",
        { asOf: w.asOf },
      );
      make.disabled = false;
      if (!r.ok) return toast("No invoices were made", r.body.error);
      const all = [...r.body.made, ...r.body.added];
      toast(
        `${all.length} draft invoice${all.length === 1 ? "" : "s"} ready to approve`,
        all.join(", "),
      );
      await Promise.all([loadWeek(), loadList()]);
    });
    weekSlot.replaceChildren(
      card(
        el("span", {}, icon("zap"), ` Bill the week · ${w.week.label}`),
        `A validated visit with nothing held is ready to bill. ${w.closes}`,
        [el("span", { class: "weeknav" }, prev, next, make)],
        w.rows.length
          ? table(
              [
                {
                  h: "Patient",
                  f: (r) =>
                    el(
                      "span",
                      {},
                      el("b", {}, r.patient),
                      el(
                        "div",
                        { class: "small muted" },
                        r.week ? `${r.client} · week ${r.week}` : r.client,
                      ),
                    ),
                },
                { h: "Visits", num: true, f: (r) => String(r.visits) },
                { h: "Ready", num: true, f: (r) => String(r.ready) },
                {
                  h: "Flagged",
                  f: (r) =>
                    el(
                      "span",
                      {},
                      r.flagged.length
                        ? el(
                            "span",
                            {},
                            chip(`${r.flagged.length} to fix`, "bad"),
                            el(
                              "div",
                              { class: "flaglist" },
                              ...r.flagged.map((f) => el("div", {}, f)),
                            ),
                          )
                        : chip("None", "ok"),
                      r.notice ? el("div", { class: "small" }, chip(r.notice, "warn")) : null,
                    ),
                },
                {
                  h: "Ready to bill",
                  num: true,
                  f: (r) =>
                    el(
                      "span",
                      {},
                      r.amount,
                      r.billed ? el("div", { class: "small muted" }, `${r.billed} billed`) : null,
                    ),
                },
              ],
              w.rows,
              (r) => ctx.go(`/homecare/patients/${r.patientId}`),
              "Bill the week",
            )
          : el("p", { class: "muted" }, "No visits that week."),
      ),
    );
  }

  async function loadList() {
    type Answer = PageInfo & { invoices: InvoiceRow[] };
    // Brief 1E-32: the server answers one page at a time.
    const ask = (params: URLSearchParams) =>
      api<Answer>("GET", `/api/invoices?${params.toString()}`);
    const res = await ask(firstPageParams("homecare/billing", ctx.me.user.id));
    if (!res.ok) {
      listSlot.replaceChildren(
        el("p", { class: "note", role: "alert" }, "The invoices couldn't be loaded."),
      );
      return;
    }
    const columns: Column<InvoiceRow>[] = [
      {
        id: "number",
        label: "Invoice",
        text: (r) => r.number,
        cell: (r) => el("b", {}, r.number),
        search: true,
      },
      { id: "client", label: "Client", text: (r) => r.client, search: true },
      { id: "amount", label: "Amount", text: (r) => `${r.amount} · ${r.period}` },
      {
        id: "due",
        label: "Due",
        text: (r) => r.due.text,
        cell: (r) => chip(r.due.text, r.due.level),
      },
      {
        id: "stage",
        label: "Stage",
        text: (r) => r.stage,
        cell: (r) => chip(r.stage, STAGE_LEVEL[r.stage] ?? "plain"),
        values: STAGES,
      },
    ];
    listSlot.replaceChildren(
      list<InvoiceRow>({
        id: "homecare/billing",
        userId: ctx.me.user.id,
        noun: "invoices",
        searchLabel: "Search invoices or clients",
        first: { rows: res.body.invoices, page: res.body },
        source: async (params) => {
          const r = await ask(params);
          return r.ok ? { rows: r.body.invoices, page: r.body } : null;
        },
        rowId: (r) => r.id,
        columns,
        chips: { values: STAGES, of: (r) => r.stage },
        board: {
          stages: ["Draft", "Approved", "Sent", "Paid", "Deposited"],
          stageOf: (r) => r.stage,
          tile: (r) => [r.number, r.client, `${r.amount} · ${r.period}`, r.due.text],
        },
        onOpen: (r) => ctx.go(`/homecare/billing/${r.id}`),
        empty: "No invoices yet. Make the week's invoices once a week closes.",
      }),
    );
  }

  await Promise.all([loadWeek(), loadList()]);
}

// ---- The invoice record -----------------------------------------------------------------------------

async function invoiceRecord(host: HTMLElement, ctx: ScreenContext, id: string) {
  const page = el("div", { class: "ws" });
  // Brief 1E-32: every card on the invoice folds.
  foldRecord(page, "invoice");
  host.append(page);

  async function load() {
    const res = await api<Invoice>("GET", `/api/invoices/${encodeURIComponent(id)}`);
    if (!res.ok) {
      page.replaceChildren(
        el(
          "p",
          { class: "note", role: "alert" },
          "That invoice isn't there, or this role can't open it.",
        ),
      );
      return;
    }
    draw(res.body);
  }

  async function act(path: string, body: unknown, done: string) {
    const r = await api<Invoice>("POST", `/api/invoices/${encodeURIComponent(id)}/${path}`, body);
    if (!r.ok) {
      toast("That didn't go through", r.body.error);
      return false;
    }
    toast(done);
    draw(r.body);
    return true;
  }

  function voidModal() {
    const why = area("Why is it voided?", "");
    const go = el("button", { type: "button", class: "btn pri" }, "Void invoice");
    const m = openModal({
      title: "Void this invoice",
      sub: "Its lines can be billed again on a new invoice.",
      body: [el("div", { class: "form", style: "max-width:none" }, why.el)],
      actions: [go],
    });
    why.input.focus();
    go.addEventListener("click", async () => {
      go.disabled = true;
      const r = await api<Invoice>("POST", `/api/invoices/${encodeURIComponent(id)}/void`, {
        reason: why.input.value,
      });
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      toast("Invoice voided", "Its lines can be billed again");
      draw(r.body);
    });
  }

  function depositModal() {
    const on = el("input", {
      type: "date",
      id: "dep-on",
      max: new Date().toISOString().slice(0, 10),
    });
    const go = el("button", { type: "button", class: "btn pri" }, "Mark deposited");
    const m = openModal({
      title: "Mark deposited",
      sub: "The day the payment was deposited.",
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el("div", { class: "field" }, el("label", { for: "dep-on" }, "Deposited on"), on),
        ),
      ],
      actions: [go],
    });
    on.focus();
    go.addEventListener("click", async () => {
      go.disabled = true;
      const r = await api<Invoice>("POST", `/api/invoices/${encodeURIComponent(id)}/deposited`, {
        on: on.value,
      });
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      toast("Marked deposited");
      draw(r.body);
    });
  }

  async function sendPayLink(v: Invoice) {
    const r = await api<{ file: { id: string; name: string }; document: string }>(
      "POST",
      `/api/invoices/${encodeURIComponent(id)}/pay-link`,
    );
    if (!r.ok) return toast("No pay link was made", r.body.error);
    void openSend({
      files: [r.body.file],
      document: r.body.document,
      for: "Billing",
      tab: "Email",
      onSent: () => {
        toast("Pay link sent", `${v.payOnline?.open ?? ""} · card or ACH, on Stripe's page`);
        void load();
      },
    });
  }

  /**
   * Email the invoice (brief 1E-18): the Send sheet with the invoice and, as the client sets, the
   * period's visit notes in their own link, attached, or none.
   */
  async function emailInvoice(v: Invoice) {
    const r = await api<{
      files: { id: string; name: string }[];
      document: string;
      notesLink: { fileIds: string[]; title: string } | null;
    }>("POST", `/api/invoices/${encodeURIComponent(id)}/email`);
    if (!r.ok) return toast("The invoice can't be emailed yet", r.body.error);
    void openSend({
      files: r.body.files,
      document: r.body.document,
      for: "Billing",
      tab: "Email",
      alsoLink: r.body.notesLink,
      onSent: () => {
        toast("Invoice emailed", v.number);
        void load();
      },
    });
  }

  function draw(v: Invoice) {
    ctx.crumb(v.number);
    const actions = [
      ib("eye", "Preview", () => void previewModal(id)),
      ib("download", "Download PDF", () => window.open(`/api/invoices/${id}/pdf`, "_blank")),
      ...(v.status === "approved" || v.status === "sent"
        ? [ib("mail", "Email the invoice", () => void emailInvoice(v))]
        : []),
      ...(v.can.void ? [ib("x-circle", "Void with a reason", voidModal)] : []),
    ];
    const head = el(
      "div",
      { class: "head" },
      el(
        "div",
        { class: "t" },
        el("div", { class: "eyebrow" }, "Bill"),
        el("h1", {}, icon("file", "h1i"), v.number),
        el("div", { class: "muted" }, `${v.client.name} · ${v.period} · ${v.format}`),
      ),
      el("div", { class: "actions" }, ...actions),
    );
    const cur = v.stage;
    const main = v.can.approve
      ? el("button", { type: "button", class: "btn pri" }, icon("check"), " Approve")
      : v.can.send
        ? el(
            "button",
            { type: "button", class: "btn pri" },
            icon("check"),
            " Complete · move to Sent",
          )
        : v.can.deposit
          ? el("button", { type: "button", class: "btn pri" }, icon("check"), " Mark deposited")
          : null;
    main?.addEventListener("click", () => {
      if (v.can.deposit) return depositModal();
      main.disabled = true;
      void (
        v.can.approve
          ? act("approve", {}, "Approved and locked")
          : act("send", {}, "Marked sent: the due date is set")
      ).finally(() => (main.disabled = false));
    });
    const stage =
      v.status === "void"
        ? el(
            "p",
            { class: "note warnnote", role: "status" },
            `Void · ${v.void?.at ?? ""}: ${v.void?.reason ?? ""}. Its lines were freed to be billed again.`,
          )
        : el(
            "section",
            { class: "card stagebox", "aria-label": "Stage" },
            el(
              "div",
              {},
              el("div", { class: "eyebrow" }, `Stage ${cur + 1} of ${v.stages.length}`),
              el("h2", {}, v.stages[cur] ?? ""),
            ),
            el(
              "ol",
              { class: "stepper" },
              ...v.stages.map((s, i) =>
                el(
                  "li",
                  { class: i < cur ? "done" : i === cur ? "cur" : undefined },
                  el("span", { class: "bar" }),
                  el("span", { class: "lab" }, el("span", { class: "n" }, String(i + 1)), s),
                ),
              ),
            ),
            el(
              "div",
              { class: "nextstep" },
              el(
                "p",
                {},
                `${STAGE_HINT[cur] ?? ""} ${cur === 0 ? v.madeBy + "." : ""}${
                  v.deposited
                    ? ` ${v.deposited.on}${v.deposited.by ? ` · ${v.deposited.by}` : ""}.`
                    : ""
                }`.trim(),
              ),
              main,
            ),
          );
    const tile = (k: string, val: Node | string, big = false) =>
      el(
        "section",
        { class: "card" },
        el("div", { class: "muted small" }, k),
        el("div", { class: `tilev${big ? " big" : ""}` }, val),
      );
    // The CMS-1500 card (brief 1E-2), for a client billed by claim form.
    const claims = el("div");
    void invoiceClaimsCard(claims, id, v.client, ctx, () => void load());
    const packet = el("div");
    void packetCard(packet, v);
    page.replaceChildren(
      head,
      stage,
      el(
        "div",
        { class: "grid4" },
        tile("Invoice date", v.invoiceDate ?? "Not sent yet"),
        tile("Due date", v.dueDate ?? "Set when sent"),
        tile("Terms", v.terms),
        tile("Payment", chip(v.payment.text, v.payment.level)),
      ),
      el(
        "div",
        { class: "grid4" },
        tile("Billed", v.billed, true),
        tile("Received", v.received, true),
        tile("Fees withheld", v.fees, true),
        tile("Short or denied", v.short, true),
      ),
      claims,
      claimLinesCard(v),
      packet,
      ...(v.payOnline ? [payCard(v)] : []),
      card(
        "Line by line: billed and paid",
        "Each line is a service on a date, without the caregiver's name.",
        v.payments.map((p) => {
          const b = el(
            "button",
            { type: "button", class: "btn sm" },
            `${p.label} `,
            chip(
              p.status === "posted" ? "Posted" : p.status === "void" ? "Void" : "To post",
              p.status === "posted" ? "ok" : p.status === "void" ? "plain" : "warn",
            ),
          );
          b.addEventListener("click", () => ctx.go(`/homecare/payments/${p.id}`));
          return b;
        }),
        table(
          [
            { h: "Who", f: (l) => el("b", {}, l.who) },
            { h: "Date", f: (l) => l.date },
            { h: "In – out", f: (l) => l.inOut },
            {
              h: "Description",
              f: (l) =>
                el(
                  "span",
                  {},
                  l.description,
                  l.voided ? el("span", {}, " ", chip("Voided")) : null,
                ),
            },
            { h: "Units", num: true, f: (l) => l.units },
            { h: "Bill rate", num: true, f: (l) => l.rate },
            { h: "Billed", num: true, f: (l) => el("b", {}, l.billed) },
            { h: "Paid", num: true, f: (l) => l.paid },
            { h: "Fee", num: true, f: (l) => l.fee },
            {
              h: "Status",
              f: (l) => (l.status ? chip(l.status.text, l.status.level) : ""),
            },
            { h: "Reason", f: (l) => l.reason },
          ],
          // Brief 1E-16: each differential as its own line under its visit's line.
          v.lines.flatMap((l) => [
            l,
            ...(l.parts ?? []).map((p) => ({
              ...l,
              who: "",
              date: "",
              inOut: "",
              description: p.words,
              units: "",
              rate: "",
              billed: p.billed,
              paid: "",
              fee: "",
              status: null,
              reason: "",
              parts: [],
            })),
          ]),
          (l) => ctx.go(`/homecare/patients/${l.patientId}`),
          "Invoice lines",
        ),
      ),
    );
  }

  /** Claim lines (brief 1E-35, mockup v93): by day, the day's shifts and caregivers under it. */
  function claimLinesCard(v: Invoice) {
    const c = v.claimLines;
    const showCode = c.lines.some((l) => l.code);
    const k = card(
      el("span", {}, icon("list-unordered"), ` ${c.title}`),
      c.sub,
      [],
      el(
        "dl",
        { class: "kv" },
        el("dt", {}, "Bill to"),
        el("dd", {}, c.billTo),
        el("dt", {}, "Period"),
        el("dd", {}, c.period),
      ),
      table(
        [
          {
            h: c.how === "week" ? "Week" : "Day",
            f: (l) =>
              el(
                "span",
                {},
                el("b", {}, l.day),
                l.shifts.length && c.how !== "visit"
                  ? el("div", { class: "small muted" }, l.shifts.join(" · "))
                  : l.shifts.length
                    ? el("div", { class: "small muted" }, l.shifts[0] ?? "")
                    : null,
              ),
          },
          { h: "Service", f: (l) => l.service },
          ...(showCode
            ? [
                { h: "Code", f: (l: (typeof c.lines)[number]) => l.code },
                { h: "Place", f: (l: (typeof c.lines)[number]) => l.place },
              ]
            : []),
          { h: "Units", num: true, f: (l) => l.units },
          { h: "Bill rate", num: true, f: (l) => l.rate },
          { h: "Amount", num: true, f: (l) => el("b", {}, l.amount) },
        ],
        c.lines,
        undefined,
        "Claim lines",
      ),
      el(
        "p",
        { class: "small muted", style: "margin:6px 0 0" },
        "Payroll shows the same shifts by caregiver and hours.",
      ),
    );
    k.setAttribute("aria-label", "Claim lines");
    return k;
  }

  /**
   * The billing packet (brief 1E-35, mockup v93): each page, a missing note named, Download as one
   * PDF, and Send packet, which asks first when a note is missing.
   */
  async function packetCard(slot: HTMLElement, v: Invoice) {
    const r = await api<Packet>("GET", `/api/invoices/${encodeURIComponent(id)}/packet`);
    if (!r.ok) return;
    const p = r.body;
    const download = el(
      "button",
      { type: "button", class: "btn sm" },
      icon("download"),
      " Download",
    );
    download.addEventListener("click", async () => {
      download.disabled = true;
      const d = await api<{ file: { id: string; name: string } }>(
        "POST",
        `/api/invoices/${encodeURIComponent(id)}/packet/pdf`,
      );
      download.disabled = false;
      if (!d.ok) return toast("The packet wasn't made", d.body.error);
      toast("Packet downloaded", "One PDF: the claim, then each note by date");
      void fileDownload(d.body.file.id, d.body.file.name);
    });
    const send = el(
      "button",
      { type: "button", class: "btn sm pri" },
      icon("paper-airplane"),
      " Send packet",
    );
    const ready = v.status !== "draft" && v.status !== "void";
    send.disabled = !ready;
    if (!ready) send.setAttribute("data-tip", "Approve the invoice first");
    const go = async (confirm: boolean) => {
      const s = await api<{
        files: { id: string; name: string }[];
        document: string;
        missing?: string;
        ask?: boolean;
      }>("POST", `/api/invoices/${encodeURIComponent(id)}/packet/send`, { confirm });
      if (!s.ok) {
        if (s.status === 409 && s.body.ask) return askMissing(s.body.missing ?? "");
        return toast("The packet wasn't sent", s.body.error);
      }
      void openSend({
        files: s.body.files,
        document: s.body.document,
        for: "Billing",
        tab: "Email",
        onSent: () => {
          toast("Packet sent", s.body.missing ? `Missing: ${s.body.missing}` : v.number);
          void load();
        },
      });
    };
    const askMissing = (missing: string) => {
      const wait = el("button", { type: "button", class: "btn" }, "Wait for the note");
      const anyway = el(
        "button",
        { type: "button", class: "btn pri" },
        icon("paper-airplane"),
        " Send anyway",
      );
      const m = openModal({
        title: "Send the packet with a note missing?",
        sub: missing,
        body: [
          el(
            "p",
            {},
            "The payer denies a line without its note. Send now and resubmit that line later, or wait for the note.",
          ),
        ],
        actions: [wait, anyway],
      });
      wait.addEventListener("click", () => m.close());
      anyway.addEventListener("click", () => {
        m.close();
        void go(true);
      });
    };
    send.addEventListener("click", () => {
      send.disabled = true;
      void go(false).finally(() => (send.disabled = !ready));
    });
    const c = card(
      el("span", {}, icon("package"), " Billing packet"),
      p.summary,
      p.canSend ? [download, send] : [],
      ...(p.missing
        ? [
            el(
              "div",
              { class: "note" },
              el(
                "p",
                {},
                chip("Missing", "bad"),
                ` ${p.missing.replace(/\.$/, "")}. The payer denies a line without its note.`,
              ),
            ),
          ]
        : []),
      table(
        [
          { h: "Page", f: (x) => el("b", {}, x.page) },
          { h: "What", f: (x) => x.what },
          {
            h: "",
            f: (x) =>
              x.status === "missing"
                ? chip("Missing", "bad")
                : x.status === "waiting"
                  ? chip("Approve first", "warn")
                  : x.status === "in"
                    ? chip("Note in", "ok")
                    : chip("Ready", "ok"),
          },
        ],
        p.pages,
        undefined,
        "Billing packet",
      ),
    );
    c.setAttribute("aria-label", "Billing packet");
    slot.replaceChildren(c);
  }

  /** Get paid online (Stripe connected): a pay link for what's open, sent through the Send sheet. */
  function payCard(v: Invoice) {
    const p = v.payOnline as NonNullable<Invoice["payOnline"]>;
    const send = el(
      "button",
      { type: "button", class: "btn sm" },
      icon("link"),
      " Send a pay link",
    );
    send.disabled = !p.can;
    if (!p.can && p.why) send.setAttribute("data-tip", p.why);
    send.addEventListener("click", () => {
      send.disabled = true;
      void sendPayLink(v).finally(() => (send.disabled = !p.can));
    });
    const prev = el(
      "button",
      { type: "button", class: "btn sm" },
      icon("file"),
      " Preview and print invoice",
    );
    prev.addEventListener("click", () => void previewModal(id));
    const c = card(
      el("span", {}, icon("credit-card"), " Get paid online"),
      "Stripe · card or ACH · the payment posts back to this invoice line by line",
      [send, prev],
      p.links.length
        ? table(
            [
              { h: "Sent", f: (k) => k.sent },
              { h: "Amount", num: true, f: (k) => k.amount },
              { h: "Expires", f: (k) => k.expires },
              {
                h: "Status",
                f: (k) =>
                  chip(
                    k.status === "open"
                      ? "Open"
                      : k.status === "paid"
                        ? "Paid"
                        : k.status === "expired"
                          ? "Expired"
                          : "Void",
                    k.status === "paid" ? "ok" : k.status === "open" ? "info" : "plain",
                  ),
              },
            ],
            p.links,
            undefined,
            "Pay links",
          )
        : el("p", { class: "small muted" }, p.can ? `Open: ${p.open}` : (p.why ?? "")),
    );
    c.setAttribute("aria-label", "Get paid online");
    return c;
  }

  await load();
}

/** The invoice on the business's letterhead, as it will print; with the PDF. */
export async function previewModal(id: string) {
  const res = await api<Letter>("GET", `/api/invoices/${encodeURIComponent(id)}/preview`);
  if (!res.ok) return toast("The preview couldn't be made", res.body.error);
  const l = res.body;
  const pdf = el("button", { type: "button", class: "btn" }, icon("download"), " Download PDF");
  pdf.addEventListener("click", () => window.open(`/api/invoices/${id}/pdf`, "_blank"));
  const print = el("button", { type: "button", class: "btn" }, icon("file"), " Print");
  print.addEventListener("click", () => {
    const w = window.open(`/api/invoices/${id}/pdf`, "_blank");
    w?.addEventListener("load", () => w.print());
  });
  openModal({
    title: `Invoice ${l.number}`,
    sub: l.draft ? "Draft: this is how it will look once approved and sent." : l.status,
    wide: true,
    className: "invprev",
    body: [
      el(
        "div",
        { class: "letter", "aria-label": "Invoice preview" },
        el(
          "div",
          { class: "lh" },
          el(
            "div",
            {},
            el("b", {}, l.business.name),
            l.business.line ? el("div", { class: "small" }, l.business.line) : null,
            ...l.business.address.map((a) => el("div", { class: "small" }, a)),
            el(
              "div",
              { class: "small" },
              [l.business.phone, l.business.npi ? `NPI ${l.business.npi}` : null]
                .filter(Boolean)
                .join(" · "),
            ),
          ),
          el(
            "div",
            { style: "text-align:right" },
            el("b", {}, `INVOICE ${l.number}`),
            el("div", {}, l.period),
            el("div", {}, `${l.issued} · ${l.terms}`),
            l.due ? el("div", {}, `Due ${l.due}`) : null,
          ),
        ),
        el(
          "div",
          {},
          el("b", {}, "Bill to"),
          el("div", {}, l.billTo.name),
          l.billTo.vendor ? el("div", {}, l.billTo.vendor) : null,
          ...l.billTo.address.map((a) => el("div", {}, a)),
          l.billTo.contract ? el("div", {}, `Contract ${l.billTo.contract}`) : null,
        ),
        ...l.patients.map((p) =>
          el(
            "table",
            { class: "ivt" },
            el(
              "thead",
              {},
              el(
                "tr",
                { class: "ivg" },
                el(
                  "td",
                  { colspan: "5" },
                  el("b", {}, `Patient: ${p.patient}`),
                  p.birthDate ? ` · DOB ${p.birthDate}` : "",
                  p.claim ? ` · Claim ${p.claim}` : "",
                ),
              ),
              el(
                "tr",
                {},
                el("th", {}, "Date"),
                el("th", {}, "Service"),
                el("th", {}, "Units"),
                el("th", { class: "num" }, "Bill rate"),
                el("th", { class: "num" }, "Amount"),
              ),
            ),
            el(
              "tbody",
              {},
              ...p.lines.map((x) =>
                el(
                  "tr",
                  {},
                  el("td", {}, x.date),
                  el(
                    "td",
                    {},
                    x.service,
                    x.shifts?.length
                      ? el("div", { class: "small muted" }, x.shifts.join(" · "))
                      : null,
                  ),
                  el("td", {}, x.units),
                  el("td", { class: "num" }, x.rate),
                  el("td", { class: "num" }, x.amount),
                ),
              ),
            ),
            el(
              "tfoot",
              {},
              el(
                "tr",
                {},
                el("td", { colspan: "4" }, "Patient total"),
                el("td", { class: "num" }, el("b", {}, p.total)),
              ),
            ),
          ),
        ),
        el("div", { class: "lh" }, el("b", {}, "Total"), el("b", { "data-total": "" }, l.total)),
        el("div", { class: "ft" }, `${l.business.name} · questions ${l.business.phone ?? ""}`),
      ),
    ],
    actions: [print, pdf],
  });
}
